import type { Phase } from '@concord/domain';

/**
 * In-app help: one topic per screen, written for the person who uses that screen. Keep the
 * wording in step with the screen; `help.test.ts` checks every route has a topic.
 */
export interface HelpTopic {
  title: string;
  /** One or two sentences: what this screen is for. */
  summary: string;
  sections: { heading: string; points: string[] }[];
  /** Features that appear on this screen but arrive in a later phase. */
  later?: { phase: Phase; text: string }[];
  /** Diagrams (apps/web/public/diagrams), shown after the sections. */
  figures?: { src: string; alt: string; caption: string }[];
}

const OBJECTS_FIGURE = {
  src: '/diagrams/concord-objects.svg',
  alt: 'Customer, plan, patient, case, panel, consultation, then minutes and earnings in Phase 3; beside them the plan network, physician pools and physicians',
  caption: 'How the parts fit together.',
};
const WORKFLOW_FIGURE = {
  src: '/diagrams/case-workflow.svg',
  alt: 'A case moves from Intake in progress to Awaiting release, Awaiting panel, Scheduled, Awaiting closure and Closed, with On hold and Canceled beside it',
  caption: 'A case from intake to closure, and what moves it on.',
};

export const HELP_IDS = [
  'sign-in',
  'console-sign-in',
  'welcome',
  'patient-home',
  'patient-intake',
  'console-home',
  'console-cases',
  'console-panels',
  'console-consultations',
  'console-case',
  'console-panel',
  'console-intake',
  'console-search',
  'console-directory',
  'console-account',
  'console-feedback',
  'console-audit',
  'console-security',
  'console-plans',
  'console-pools',
  'physician-link',
  'general',
] as const;
export type HelpId = (typeof HELP_IDS)[number];

const SIGN_IN_POINTS = [
  'Sign in as switches between the patient portal and the care navigator console. Physicians do not sign in yet: they use the secure link in their invitation email.',
  'For privacy, you are signed out after 15 minutes without activity. A minute before, Concord asks whether you are still there.',
  'Enter your email address and choose Email me a code. There is no password.',
  'The email has a 6-digit code and a sign-in link. Type the code here, or open the link on this device. Either works once.',
  'Codes last 10 minutes. After five wrong tries, ask for a new code.',
  'No email? Check spam, then ask for a new code. For your privacy the screen looks the same whether or not the address has an account.',
  'Signing in with the emailed code also confirms your email address. Every account needs a confirmed address.',
];

export const HELP: Record<HelpId, HelpTopic> = {
  'sign-in': {
    title: 'Signing in',
    summary: 'Cancer Expert Now signs you in with a code sent to your email instead of a password.',
    sections: [{ heading: 'How it works', points: SIGN_IN_POINTS }],
  },
  'console-sign-in': {
    title: 'Signing in to the console',
    summary:
      'The console is for Cancer Expert Now staff. You sign in with your email and the code from your authenticator app, or with an emailed code followed by the app code. Never a password.',
    sections: [
      { heading: 'How it works', points: SIGN_IN_POINTS },
      {
        heading: 'Authenticator app',
        points: [
          'Quickest: choose Use my authenticator app, then enter your email and the code the app shows. No email is sent. This works once your email is verified and the app is set up.',
          'Or choose Email me a code; after the emailed code, Concord asks for the app code too. You set the app up when you open your account.',
        ],
      },
      {
        heading: 'Staying signed in',
        points: ['A console session lasts 12 hours. Sign out from the top right on shared computers.'],
      },
    ],
  },
  welcome: {
    title: 'Setting up your account',
    summary:
      'This link came from your care navigator. Confirm your details to finish setting up your account.',
    sections: [
      {
        heading: 'What to expect',
        points: [
          'Open my account emails a 6-digit code to your address. Enter it to confirm your email; the account opens. Send a new code if it does not arrive.',
          'The link works once and expires. If it has expired, ask your care navigator to send a new one.',
          'You will not choose a password. Each time you sign in, we email you a code.',
          'Staff then set up an authenticator app (Google or Microsoft Authenticator) before using the console.',
        ],
      },
    ],
  },
  'patient-home': {
    title: 'Your home page',
    summary:
      'Everything about your case in one place: what happens next, your consultation, and your records.',
    sections: [
      {
        heading: 'On this page',
        points: [
          'Your consultation: the date and time in your time zone, and who you are meeting. Add it to your calendar from here.',
          'Join the consultation: the button opens your Zoom meeting 15 minutes before the start. Until then it shows when it opens.',
          'Your case: where it stands in plain words, and whether anything is waiting on you.',
          'Your records: every file you uploaded, and which physicians it was shared with. Download any of them.',
          'Start a new case whenever your plan covers it. You can stop part way and come back from any device.',
        ],
      },
      {
        heading: 'Your consultation on Zoom',
        points: [
          'The consultation is a Zoom video call. Every email about it has a Join on Zoom button, and the calendar file holds the same link.',
          'You can join up to 15 minutes early, from the email or from this page. You do not need a Zoom account or a passcode.',
          'You can join with your camera off if you prefer audio only.',
          'Test your camera, microphone and speakers beforehand at zoom.us/test.',
          'Please keep the link to yourself: anyone with it can join.',
        ],
      },
      {
        heading: 'Getting help',
        points: [
          'Contact your care navigator (top right) shows their phone number and email. They run your case and can answer anything about it.',
          'For anything urgent about your health, contact your own doctor.',
        ],
      },
    ],
    later: [
      {
        phase: 'P2',
        text: 'Test your camera and microphone from this page before the consultation.',
      },
      { phase: 'P4', text: 'Message your care navigator and panel inside Concord.' },
    ],
  },
  'patient-intake': {
    title: 'Sending your case',
    summary:
      'Seven short steps collect what the physicians need. Everything saves as you go, so you can stop and come back from any device.',
    sections: [
      {
        heading: 'The steps',
        points: [
          'Case type: start typing your diagnosis and pick the closest match.',
          'Expert panel: shown only if your plan offers a choice of physician group.',
          'About you: age and sex help the physicians. Both are optional.',
          'History and questions: tell your story in your own words, then list the questions you want answered. The writing assistant can point out what is missing and suggest questions; nothing changes unless you accept it.',
          'Records: add reports, scans and lab results. Each file needs a short description. The accepted types and size limit are shown before you choose files.',
          "Times you're free: pick the times you could meet, in your time zone. More choices make it quicker to find physicians who can join.",
          'Review and send: check everything, accept the terms, and send. You will get a case reference by email.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'The summary on the side shows which steps are done. Select any step to go back to it.',
          'You can change or remove records until your navigator shares the case with physicians. After that they are locked so everyone sees the same files.',
        ],
      },
    ],
  },
  'console-home': {
    title: 'Dashboard',
    summary: 'Your day at a glance: what needs you now, and what is scheduled this week.',
    sections: [
      {
        heading: 'On this page',
        points: [
          'Needs attention counts cases waiting on a navigator: to release, to chase, to confirm or to close.',
          'The queue chips (Awaiting release, Awaiting panel, Scheduled today, Scheduled this week, Awaiting closure) open the case list already filtered.',
          'Cases needing attention lists each case with the reason in words. Select a row to open the case.',
          "This week's consultations shows who meets when.",
          'New case starts a case for a patient who called in.',
        ],
      },
      {
        heading: 'How it fits together',
        points: [
          'A customer (the employer) has a plan, and the plan covers its patients. A patient is on one plan at a time and can send many cases.',
          'Each case belongs to one patient and one cancer type, and has its own panel: the physicians you invite to that case. A physician can sit on many panels.',
          'Release needs at least one physician on the panel, at least one time the patient offered, and valid coverage.',
          'The case gets its consultation when a physician accepts a time (held, provisional); it is confirmed when the panel fills or you confirm it. One consultation per case.',
          'After the consultation is held, each physician who took part is paid for their minutes at their rate. That record arrives in Phase 3; until then minutes and payments are kept outside Concord.',
          "The plan's network decides in plan or out of plan. Physician pools are pick lists by specialty and cancer type; they do not change coverage.",
        ],
      },
      {
        heading: 'Shortcuts',
        points: [
          'Press / anywhere in the console to search cases, people and reference numbers.',
          'Collapse the side navigation with the button next to the Concord name. It stays the way you leave it.',
          'Your name, top right, then User guide opens the full navigator guide with pictures of every screen.',
        ],
      },
    ],
    later: [
      { phase: 'P3', text: 'Awaiting minutes, consultation minutes and physician payables.' },
      {
        phase: 'P5',
        text: 'Coverage monitoring that warns when a case type is running short of physicians.',
      },
    ],
    figures: [OBJECTS_FIGURE, WORKFLOW_FIGURE],
  },
  'console-cases': {
    title: 'Cases',
    summary: 'Every case, searchable and filtered by the work it needs.',
    sections: [
      {
        heading: 'Finding cases',
        points: [
          'Work queues: Needs attention, Awaiting release, Awaiting panel, Scheduled today, Scheduled this week and Awaiting closure. Each is a saved filter.',
          'Search by CX reference, patient name or email. Quality review only shows cases flagged for review.',
          'Filters stay as you left them when you come back to the list.',
          'Select a row to open the case screen.',
        ],
      },
    ],
    later: [{ phase: 'P3', text: 'Awaiting minutes queue.' }],
  },
  'console-panels': {
    title: 'Panels',
    summary: 'Cases released to physicians and waiting for their answers.',
    sections: [
      {
        heading: 'Working this list',
        points: [
          'Each row shows how many physicians were invited and how many replied.',
          'Open a case to remind a physician, reset their 24-hour clock, or add another physician.',
        ],
      },
    ],
  },
  'console-consultations': {
    title: 'Consultations',
    summary: 'Consultations scheduled this week, with their times and panels.',
    sections: [
      {
        heading: 'Working this list',
        points: [
          'Open a case to see its Zoom meeting or change the join link, move the consultation, or mark it as held.',
          'Reminders with the join link go to everyone 24 hours and 1 hour before, automatically.',
        ],
      },
    ],
    later: [{ phase: 'P2', text: 'Zoom recordings and transcripts kept with the case.' }],
  },
  'console-case': {
    title: 'The case screen',
    summary:
      'One screen runs the case from submission to closure. Every action here sends the right email to the right people and is recorded in the event log.',
    sections: [
      {
        heading: 'Reading the case',
        points: [
          'The banner at the top says what the case is waiting for and offers the action that clears it.',
          'The coverage chain (customer, plan, network, funding) shows whether the patient is covered. A broken link is named.',
          'The case card holds the history and questions as the patient sent them. Records open from the Records panel; every download is logged.',
          "Add a record attaches a file that reached you outside Concord (fax, email, a disc) and records where it came from. Once the case is released, it goes straight to the physicians and can't be changed.",
          'Terms accepted shows the version the patient agreed to; select it to read that exact text. An accepted physician shows the engagement terms version they acknowledged, which opens the same way.',
          'The counters show how many physicians were invited, replied, are still open (awaiting reply), accepted and declined.',
          'Times the patient offered are on the right, in your time zone. The consultation panel shows the time as each person sees it.',
        ],
      },
      {
        heading: 'Physicians',
        points: [
          'Build the panel picks physicians and releases the case. Each physician gets an email with a secure link, never case details.',
          "The participants list shows each physician's answer. The arrow next to an invited physician sends a reminder, resets their 24-hour clock or withdraws the invitation.",
          'When a physician accepts, their time is held. Confirm it, or set a different time.',
        ],
      },
      {
        heading: 'Scheduling',
        points: [
          'Confirming a time emails the patient, the physicians and any guests, each in their own time zone with a calendar file.',
          'Reschedule moves the consultation; everyone is emailed and both times stay in the log.',
          'After the session, Mark as held, then Close case. Closed cases are read-only.',
        ],
      },
      {
        heading: 'Zoom',
        points: [
          'Concord makes the Zoom meeting when a time is held and sends its Join on Zoom link to the patient, the physicians and guests with every confirmation and reminder. Calendar files carry it too.',
          'The consultation panel shows the meeting number and Open in Zoom. Moving the time moves the meeting and keeps the link; canceling the case deletes it.',
          'Everyone can join up to 15 minutes early without a host; the passcode is in the link. Zoom records the call to its cloud.',
          'Making the Zoom meeting means Zoom has not answered yet: Concord keeps trying, and everyone gets a Your join link is ready email when it does.',
          'If Zoom fails after every retry, the panel says so: Try Zoom again, or paste a Zoom link into the join link box. A pasted link replaces the Zoom meeting, which is deleted, and is emailed to everyone.',
          'No patient name or case detail goes to Zoom: every meeting is named Cancer Expert Now consultation.',
        ],
      },
      {
        heading: 'Other actions',
        points: [
          'Add internal note: staff only, never shown to the patient or physicians.',
          'More: add a physician or guest, change the setup, reassign the case to another care navigator, flag for quality review, prevent invitations, copy the case link, or cancel with a reason.',
          'Reassign case makes another navigator the owner. The patient then sees them as their care navigator, and their name goes on physician invitations. The change is logged.',
          'Show as participants see it hides internal events so you can see what the patient sees.',
          'Phone numbers and the date of birth are masked. Show reveals one for a minute while you work; each reveal is recorded in the audit log.',
          'Admins see Removed items at the bottom: records and guests that were removed, kept for recovery, with Restore.',
        ],
      },
    ],
    later: [
      { phase: 'P2', text: 'Zoom recordings and transcripts kept with the case.' },
      { phase: 'P3', text: 'Consultation minutes and physician earnings recorded on the case.' },
      { phase: 'P4', text: 'A live channel for questions between the patient, navigator and panel.' },
    ],
    figures: [WORKFLOW_FIGURE, OBJECTS_FIGURE],
  },
  'console-panel': {
    title: 'Build the panel',
    summary: 'Choose the physicians for this case. Nobody is contacted until you release.',
    sections: [
      {
        heading: 'Choosing physicians',
        points: [
          'Candidates are the physicians associated with the case type. Or choose a physician pool to pick from its physicians instead; pools for this cancer type are listed first.',
          'Narrow by specialty or search by name.',
          'Each row shows plan status, how many open invitations the physician already has, and whether they are free at the times the patient offered.',
          'In plan physicians can be added directly. Out of plan needs an override, and the reason is logged.',
          'Physicians already on the panel are listed below the table, not in it.',
        ],
      },
      {
        heading: 'Releasing',
        points: [
          'Set the panel size and how many responses you expect. Inviting more than the panel size covers physicians who do not reply.',
          "Release case sends every invitation at once. The records lock so every physician sees the same files. Links expire 24 hours after they're sent.",
          'When the panel fills, physicians who have not answered are told the panel filled, with no fault on their side.',
        ],
      },
    ],
    later: [
      { phase: 'P5', text: 'Prior consultations, acceptance rate and response time on each candidate.' },
    ],
  },
  'console-intake': {
    title: 'New case by phone',
    summary: 'Enter a case for a patient who called in. It is the same seven steps the patient sees.',
    sections: [
      {
        heading: 'Good to know',
        points: [
          'The case is marked as submitted by you on the patient’s behalf.',
          'Everything saves as you go. You can finish later from the case list.',
          'The patient gets the same emails as if they had sent the case themselves.',
        ],
      },
    ],
  },
  'console-search': {
    title: 'Search',
    summary: 'Results for cases and people in one list.',
    sections: [
      {
        heading: 'Tips',
        points: [
          'A CX reference in the top search goes straight to that case.',
          'Phone numbers match whatever the punctuation.',
          'Press / from anywhere in the console to search again.',
        ],
      },
    ],
  },
  'console-directory': {
    title: 'Directory',
    summary:
      'Every account: patients, physicians and staff, including people invited who have not signed up yet.',
    sections: [
      {
        heading: 'Finding people',
        points: [
          'Search by name, email, phone, account ID or CX reference.',
          'Filter by role, plan and registration. Filters show as chips you can remove. Test accounts are hidden unless you include them.',
          'Invite account sends a registration link. You can also copy the link to give it by phone. Opening it emails a code to the invited address, which must be entered, so the link alone opens nothing.',
          'Staff accounts must use a cancerexpertnow.com or cancerexpert.now address. Patients and physicians can use any address.',
        ],
      },
    ],
  },
  'console-account': {
    title: 'Account record',
    summary: 'Everything about one account on one screen, as you would read it back on a call.',
    sections: [
      {
        heading: 'On this page',
        points: [
          'Identity, status, registration, time zone, last login and invalid login count.',
          'Coverage: plan membership and whether the coverage chain is complete.',
          "The account's cases.",
          'Reissue invitation (if they never registered), and Disable or Enable account. Disabling signs the person out everywhere. You cannot disable your own account.',
          'For a care navigator: the open cases they own. Hand over open cases moves them all to another navigator, for example while they are away. Disabling a navigator asks who takes their open cases first.',
          'Phone, mobile, city and last sign-in location are masked. Show reveals one for a minute; every reveal is recorded.',
          "Plan membership shows a patient's current and earlier plans. With account management you can Change plan (optionally moving their open cases) or End coverage on a date; both need a reason and are logged.",
          'Admins can make another navigator an admin, giving them data recovery and authenticator removal. The audit log is for super admins. Nobody can remove their own admin access.',
        ],
      },
      {
        heading: 'Super admin',
        points: [
          'Edit account changes the name, display name, email, phone, mobile, city, country, time zone and role (patient or care navigator). Physician accounts keep their role.',
          'Changing the email tells both the old and new address and signs the person out everywhere; the new address is confirmed when they next sign in with a code sent to it. Changing the role also signs them out; the new role starts from its default permissions. A care navigator must have a cancerexpertnow.com or cancerexpert.now address.',
          'Permissions: tick what a care navigator may do, including admin and super admin. Nobody can remove their own super admin access, and at least one super admin always stays.',
          'Authenticator app: Remove clears a lost or replaced phone; after their next emailed-code sign-in, the person must set up the app again before using the console.',
          'Every change is recorded in the audit log by field name, never by value.',
        ],
      },
    ],
    later: [
      { phase: 'P3', text: 'Physician earnings.' },
      { phase: 'P5', text: 'Physician specialties, disclosures and unavailability.' },
    ],
  },
  'console-plans': {
    title: 'Customers & plans',
    summary:
      "Every customer's plans: what each covers, who is on it now and how many cases it carries. Read-only for now.",
    sections: [
      {
        heading: 'Reading the list',
        points: [
          'Each customer has its plans: their dates and status, funding source, physician networks (the default is marked), the case types covered, panel size and consultation length.',
          'Members counts patients whose current plan this is today. Select the number to see them in the directory.',
          'Open cases and All cases count cases sent under the plan; drafts are not counted.',
          'Download CSV saves the whole list for a spreadsheet.',
        ],
      },
      {
        heading: "A patient's plan",
        points: [
          "Change a patient's plan, or end their coverage, from their account record (Plan membership). It needs account management and a reason, and it is logged.",
          'Changing the plan can move their open cases to the new plan too, unless the new plan does not cover the case type.',
        ],
      },
    ],
    later: [
      {
        phase: 'P5',
        text: 'Adding and editing customers, plans, networks and the case types each plan covers.',
      },
    ],
  },
  'console-pools': {
    title: 'Physician pools',
    summary:
      'Groups of physicians by specialty and the cancer types they treat, not tied to any plan. Choose a pool on Build the panel to pick from its physicians.',
    sections: [
      {
        heading: 'Managing pools',
        points: [
          'New pool takes a name, the cancer types it serves, an optional specialty and a short description.',
          'Open a pool to add physicians (those with its specialty or cancer types come first) or remove them. Removing never touches a panel already built.',
          'Retire a pool you no longer use. It leaves Build the panel and can be restored later. Pools are never deleted.',
          'Managing pools needs account management. Everyone who can search the directory can see them.',
        ],
      },
      {
        heading: 'Pools and networks',
        points: [
          "A pool is not a plan's network. The network decides in plan or out of plan, and an out-of-plan choice is still recorded on the case.",
        ],
      },
    ],
  },
  'console-feedback': {
    title: 'Feedback',
    summary:
      'What patients, physicians and staff sent from the Feedback button. Only super admins see this page; open it from your name, top right.',
    sections: [
      {
        heading: 'Working the list',
        points: [
          'New shows reports nobody has reviewed. All includes reviewed ones.',
          'Each report shows the page it came from, who sent it if they were signed in, their IP address and browser.',
          'Mark reviewed when it has been handled. You can undo it.',
          'Page addresses are stored without secure links or sign-in codes.',
        ],
      },
    ],
  },
  'console-security': {
    title: 'Sign-in security',
    summary:
      'An authenticator app (we recommend Google Authenticator or Microsoft Authenticator, both free on the App Store and Google Play) is how staff sign in: your email and the code the app shows, with no email to wait for. Every staff account must have one.',
    sections: [
      {
        heading: 'Setting it up',
        points: [
          'Install Google Authenticator or Microsoft Authenticator from the App Store (iPhone) or Google Play (Android). The setup screen links to both.',
          'Choose Set up an authenticator app, scan the QR code with your app (or type the key shown), then enter the 6-digit code the app shows.',
          'The code changes every 30 seconds. Each code works once.',
          'Then sign in with Use my authenticator app: your email and the current code. If you use an emailed code instead, Concord also asks for the app code.',
        ],
      },
      {
        heading: 'If something changes',
        points: [
          'Staff cannot turn the app off or skip it; every staff sign-in includes the app code.',
          'Lost or replaced phone? An admin removes the app from your account record; after your next sign-in Concord asks you to set it up again.',
          'Setting up, removing, every sign-in and every app code (right or wrong) are recorded in the audit log.',
        ],
      },
    ],
  },
  'console-audit': {
    title: 'Audit log',
    summary:
      'Every sign-in, sign-out, view of patient data, change and reveal, with who, when and from where. Only super admins see it (your name, top right → Audit log), and entries cannot be edited or deleted.',
    sections: [
      {
        heading: 'Finding what happened',
        points: [
          'Search by case reference, name, account ID, IP address or action, and narrow by action and dates.',
          'Refused actions (a wrong sign-in code, a change the person was not allowed to make) are shaded.',
          'Changes list the fields that changed, never the text itself: notes and clinical details stay out of the log.',
          'Physicians who use a secure link are named, because each link belongs to one physician.',
        ],
      },
      {
        heading: 'What is recorded',
        points: [
          'Sign-ins (by code, link or invitation), refused sign-ins with the reason, sign-outs, and sign-outs after 15 idle minutes.',
          'Opening a case, an account, the physician candidates or a record; every download.',
          'Every change, assignment, removal and restore; every reveal of a masked phone number, date of birth or address.',
        ],
      },
    ],
  },
  'physician-link': {
    title: 'Answering an invitation',
    summary:
      'Cancer Expert Now invited you to a consultation. Everything you need to decide is on this page, and no account is needed.',
    sections: [
      {
        heading: 'Deciding',
        points: [
          'Read the history, questions and records on the left. Opening a record is logged with your name.',
          'The times on the right are the ones the patient offered, shown in your time zone. Change the zone if you are traveling.',
          'Accept a time, Decline (a reason is optional), or tell the navigator when you could do it instead.',
        ],
      },
      {
        heading: 'After you answer',
        points: [
          'The time is confirmed once the panel fills or the navigator confirms it. You get an email with the Join on Zoom link and a calendar file either way.',
          'Join from that email, or from the calendar entry, up to 15 minutes before the start. No host or passcode is needed. Reminders 24 hours and 1 hour before carry the link again.',
          'If the panel fills before you answer, the invitation closes. That is not recorded as a decline.',
          'The link is personal to you and expires. Do not forward it.',
        ],
      },
    ],
    later: [{ phase: 'P3', text: 'A physician account with your invitations, schedule and earnings.' }],
  },
  general: {
    title: 'Help',
    summary:
      'Cancer Expert Now connects patients with a panel of cancer specialists for a video consultation on Zoom.',
    sections: [
      {
        heading: 'Getting around',
        points: [
          'Patients: your home page shows your case, your consultation and your records.',
          'Staff: the console dashboard lists what needs you today.',
        ],
      },
    ],
  },
};

/** Which topic a screen shows. Most specific first. */
const ROUTES: readonly [RegExp, HelpId][] = [
  [/^\/console\/sign-in\/?$/, 'console-sign-in'],
  [/^\/console\/auth\/link\/?$/, 'console-sign-in'],
  [/^\/console\/verify\/?$/, 'console-sign-in'],
  [/^\/sign-in\/?$/, 'sign-in'],
  [/^\/auth\/link\/?$/, 'sign-in'],
  [/^\/welcome\/[^/]+\/?$/, 'welcome'],
  [/^\/home\/?$/, 'patient-home'],
  [/^\/cases\/[^/]+\/intake(\/[^/]+)?\/?$/, 'patient-intake'],
  [/^\/console\/cases\/[^/]+\/panel\/?$/, 'console-panel'],
  [/^\/console\/cases\/[^/]+\/?$/, 'console-case'],
  [/^\/console\/cases\/?$/, 'console-cases'],
  [/^\/console\/panels\/?$/, 'console-panels'],
  [/^\/console\/consultations\/?$/, 'console-consultations'],
  [/^\/console\/intake\/[^/]+(\/[^/]+)?\/?$/, 'console-intake'],
  [/^\/console\/search\/?$/, 'console-search'],
  [/^\/console\/directory\/[^/]+\/?$/, 'console-account'],
  [/^\/console\/directory\/?$/, 'console-directory'],
  [/^\/console\/feedback\/?$/, 'console-feedback'],
  [/^\/console\/plans\/?$/, 'console-plans'],
  [/^\/console\/pools(\/[^/]+)?\/?$/, 'console-pools'],
  [/^\/console\/audit\/?$/, 'console-audit'],
  [/^\/console\/security\/?$/, 'console-security'],
  // The brief stop on the way to the user guide.
  [/^\/console\/docs\/?$/, 'console-home'],
  [/^\/console\/?$/, 'console-home'],
  [/^\/respond\/[^/]+\/?$/, 'physician-link'],
];

export function helpIdFor(pathname: string): HelpId {
  return ROUTES.find(([re]) => re.test(pathname))?.[1] ?? 'general';
}
